"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildStakeOutputs = exports.buildTransactionOutputs = exports.getSignersIndices = exports.buildTransactionInputs = void 0;
const utils_1 = require("../utils");
const constants_1 = require("./constants");
const input_1 = require("./input");
const output_1 = require("./output");
const types_1 = require("./types");
function buildTransactionInputs(userInputs, utxoSet, signersAddresses, fees) {
    const targetAmounts = new Map();
    for (const fee of fees) {
        if (fee.amount < 1) {
            continue;
        }
        const assetId = fee.assetId;
        const amount = targetAmounts.has(assetId) ? targetAmounts.get(assetId) : BigInt(0);
        targetAmounts.set(assetId, amount + BigInt(fee.amount));
    }
    // gathering data needed to build transaction inputs
    for (const input of userInputs) {
        utils_1.TimeUtils.verifyLocktime(input.locktime);
        const assetId = input.assetId;
        const amount = targetAmounts.has(assetId) ? targetAmounts.get(assetId) : BigInt(0);
        targetAmounts.set(assetId, amount + BigInt(input.amount));
    }
    const gatheredAmounts = new Map();
    // init all asset ids of targets to avoid undefined values
    for (const assetId of targetAmounts.keys()) {
        gatheredAmounts.set(assetId, BigInt(0));
    }
    const now = utils_1.TimeUtils.now();
    const inputs = [];
    // This loop tries to gather required amounts from the provided utxo set
    // it will try to get fees costs covered first and then
    // the amounts needed to send the required values in user inputs.
    // If it encounter the case of an utxo that is not useful to the
    // transaction it will still be added in the build outputs.
    // This is not an issue but note that it may require to group some utxos together
    // for the outputs so make sure to spend them to avoid losses.
    for (let i = 0; i < utxoSet.length && !isGatheringComplete(targetAmounts, gatheredAmounts); i++) {
        const utxo = utxoSet[i];
        if (!('amount' in utxo.output)) {
            continue;
        }
        const output = utxo.output;
        const locked = output.locktime > now;
        const stakeable = output.typeId === constants_1.StakeableLockedOutputTypeId;
        // output cannot be consumed because it is timelocked
        if (locked && !stakeable) {
            throw new utils_1.InputError('cannot consume time locked utxo');
        }
        const addressIndices = getSignersIndices(signersAddresses, utxo.output.addresses);
        const input = stakeable && locked
            ? new input_1.StakeableLockedInput(output.locktime, output.amount, addressIndices, utxo)
            : new input_1.Secp256k1Input(output.amount, addressIndices, utxo);
        // The utxo will be added as an input in any case
        inputs.push(new input_1.TransferableInput(utxo.transactionId, utxo.utxoIndex, utxo.assetId, input));
        const assetId = utxo.assetId.value;
        gatheredAmounts.set(assetId, gatheredAmounts.get(assetId) + BigInt(output.amount));
    }
    const missingFunds = !isGatheringComplete(targetAmounts, gatheredAmounts);
    if (!missingFunds) {
        return inputs;
    }
    // first verify if funds are missing for tx + fee or for fee only
    // this is helpful for debugging or setting a correct max amount
    for (const fee of fees) {
        if (fee.amount < 1) {
            continue;
        }
        const assetId = fee.assetId;
        const amount = targetAmounts.has(assetId) ? targetAmounts.get(assetId) : BigInt(0);
        targetAmounts.set(assetId, amount - BigInt(fee.amount));
    }
    // if gathering is successful here then it means only fee amount is missing
    if (isGatheringComplete(targetAmounts, gatheredAmounts)) {
        throw new utils_1.InputError('provided utxo set does not have enough funds to cover fees');
    }
    // from here we are missing both fees amount but also amount for user inputs
    throw new utils_1.InputError('provided utxo set does not have enough funds to cover user inputs');
}
exports.buildTransactionInputs = buildTransactionInputs;
function isGatheringComplete(targets, gathereds) {
    for (const [key, target] of targets) {
        if (!gathereds.has(key) || gathereds.get(key) < target) {
            return false;
        }
    }
    return true;
}
function getSignersIndices(signers, addresses) {
    const indices = [];
    for (const signer of types_1.Address.uniqueAddresses(signers)) {
        for (let i = 0; i < addresses.length; i++) {
            const address = addresses[i];
            if (signer.matches(address)) {
                indices.push(i);
                break;
            }
        }
    }
    return indices;
}
exports.getSignersIndices = getSignersIndices;
function buildTransactionOutputs(userInputs, inputs, fee, changeAddress) {
    const spentAmounts = new Map();
    // add fees as already spent so they are not added in outputs
    spentAmounts.set(fee.assetId, fee.amount);
    let outputs = [];
    // adding outputs matching user inputs
    for (const input of userInputs) {
        const transferOutput = input.stakeable
            ? new output_1.StakeableLockedOutput(input.locktime, input.amount, input.threshold, types_1.Address.toAddresses(input.addresses))
            : new output_1.Secp256k1Output(input.amount, input.locktime, input.threshold, types_1.Address.toAddresses(input.addresses));
        outputs.push(new output_1.UserOutput(new types_1.AssetId(input.assetId), transferOutput, false));
        const assetId = input.assetId;
        const amount = spentAmounts.has(assetId) ? spentAmounts.get(assetId) : BigInt(0);
        spentAmounts.set(assetId, amount + BigInt(input.amount));
    }
    const availableAmounts = new Map();
    // getting the total amount spendable for each asset in provided inputs
    for (const input of inputs) {
        const assetId = input.getAssetId().value;
        const amount = availableAmounts.has(assetId) ? availableAmounts.get(assetId) : BigInt(0);
        availableAmounts.set(assetId, amount + BigInt(input.getAmount()));
    }
    outputs = mergeSecp256k1Outputs(outputs);
    // verifying that inputs have the funds to pay for the spent amounts
    // also adding extra outputs to avoid losses if we have unspent values
    for (const input of inputs) {
        const assetId = input.getAssetId().value;
        const spent = spentAmounts.has(assetId) ? spentAmounts.get(assetId) : BigInt(0);
        const available = availableAmounts.has(assetId) ? availableAmounts.get(assetId) : BigInt(0);
        if (spent > available) {
            throw new utils_1.OutputError('output would produce more than provided inputs');
        }
        if (spent === available) {
            continue;
        }
        const stakeable = input.getTypeId() === constants_1.StakeableLockedInputTypeId;
        const transferOutput = stakeable
            ? new output_1.StakeableLockedOutput(input.input.utxo.output.locktime, available - spent, 1, [
                new types_1.Address(changeAddress)
            ])
            : new output_1.Secp256k1Output(available - spent, BigInt(0), 1, [new types_1.Address(changeAddress)]);
        // adding change output to send remaining value into the change address
        outputs.push(new output_1.UserOutput(input.getAssetId(), transferOutput, true));
        // adding the spending of the change output
        const amount = spentAmounts.has(assetId) ? spentAmounts.get(assetId) : BigInt(0);
        spentAmounts.set(assetId, amount + available - spent);
    }
    return outputs;
}
exports.buildTransactionOutputs = buildTransactionOutputs;
function mergeSecp256k1Outputs(outputs) {
    const mergedOutputs = [];
    const spendings = new Map();
    for (const userOutput of outputs) {
        let key = userOutput.output.typeId.toString();
        key += userOutput.assetId.value;
        key += userOutput.output.locktime;
        key += userOutput.output.threshold;
        for (const address of userOutput.output.addresses) {
            key += address.serialize().toHex();
        }
        if (spendings.has(key)) {
            const out = spendings.get(key).output;
            out.amount += userOutput.output.amount;
        }
        else {
            mergedOutputs.push(userOutput);
            spendings.set(key, userOutput);
        }
    }
    return mergedOutputs;
}
function buildStakeOutputs(stakeData, inputs) {
    const stake = [];
    const targetStake = stakeData.amount;
    let spentStakeAmount = BigInt(0);
    let spentLockedStakeAmount = BigInt(0);
    const stakeableLockedInputs = [];
    for (const transferableInput of inputs) {
        const input = transferableInput.input;
        const remaining = targetStake - spentStakeAmount - spentLockedStakeAmount;
        const spent = input.amount > remaining ? remaining : input.amount;
        if (input.typeId === constants_1.StakeableLockedInputTypeId) {
            spentLockedStakeAmount += spent;
            stakeableLockedInputs.push(input);
        }
        else {
            spentStakeAmount += spent;
        }
        // enough inputs will be used already
        if (spentStakeAmount + spentLockedStakeAmount >= targetStake) {
            break;
        }
    }
    // output for regular stake
    if (spentStakeAmount > 0) {
        stake.push(new output_1.TransferableOutput(new types_1.AssetId(stakeData.assetId), new output_1.Secp256k1Output(spentStakeAmount, stakeData.locktime, stakeData.threshold, types_1.Address.toAddresses(stakeData.addresses))));
    }
    // output for stake that is stakeable only
    if (spentLockedStakeAmount > 0) {
        stake.push(...buildStakeStakeableLockedOutputs(stakeData, stakeableLockedInputs, spentLockedStakeAmount));
    }
    return stake;
}
exports.buildStakeOutputs = buildStakeOutputs;
function buildStakeStakeableLockedOutputs(stakeData, inputs, spentLockedStakeAmount) {
    const outputs = [];
    const locktimesMap = new Map();
    for (const input of inputs) {
        const key = input.locktime;
        if (!locktimesMap.has(key)) {
            locktimesMap.set(key, []);
        }
        const values = locktimesMap.get(key);
        values.push(input);
    }
    let totalAmount = BigInt(0);
    for (const [locktime, inputs] of locktimesMap) {
        let outputAmount = BigInt(0);
        for (const input of inputs) {
            const remaining = spentLockedStakeAmount - totalAmount;
            const isLast = input.amount > remaining;
            const amount = isLast ? remaining : input.amount;
            totalAmount += amount;
            outputAmount += amount;
            if (isLast) {
                break;
            }
        }
        outputs.push(new output_1.TransferableOutput(new types_1.AssetId(stakeData.assetId), new output_1.StakeableLockedOutput(locktime, outputAmount, stakeData.threshold, types_1.Address.toAddresses(stakeData.addresses))));
        if (spentLockedStakeAmount - totalAmount < 1) {
            break;
        }
    }
    return outputs;
}
